import type { LogLine } from '@/components/v1/cloud/logging/log-search/use-logs';
import type { V1LogLine, V1LogLineList } from '@/lib/api';

export const RUN_LOGS_PER_PAGE = 100;

function logKey(row: V1LogLine): string {
  return JSON.stringify([
    row.createdAt,
    row.taskExternalId,
    row.attempt,
    row.level,
    row.message,
    row.metadata,
  ]);
}

export function toRunLogLines(pages: V1LogLineList[]): LogLine[] {
  const seen = new Set<string>();
  return pages.flatMap((page) =>
    (page.rows ?? []).flatMap((row) => {
      const id = `log:${logKey(row)}`;
      if (seen.has(id)) {
        return [];
      }
      seen.add(id);
      return [
        {
          id,
          source: 'log' as const,
          timestamp: row.createdAt,
          line: row.message,
          level: row.level,
          metadata: row.metadata as Record<string, unknown> | undefined,
          attempt: row.attempt,
          taskExternalId: row.taskExternalId,
          taskDisplayName: row.taskDisplayName,
        },
      ];
    }),
  );
}

export function nextRunLogCursor(page: V1LogLineList): string | undefined {
  const rows = page.rows ?? [];
  if (rows.length < RUN_LOGS_PER_PAGE) {
    return undefined;
  }
  return rows[rows.length - 1].createdAt;
}

export type RunLogScope = {
  tenantId: string;
  filterKey: string;
  taskIds: string[];
};

// A run discovers tasks as it progresses, which changes the query key. Loaded
// pages may seed the new key only when they belong to a subset of its scope:
// the same tenant, the same filters and no task outside the new list, so
// every carried row is also a valid row of the new query.
export function canCarryRunLogs(
  previous: RunLogScope,
  next: RunLogScope,
): boolean {
  if (previous.tenantId !== next.tenantId) {
    return false;
  }
  if (previous.filterKey !== next.filterKey) {
    return false;
  }
  if (previous.taskIds.length === 0) {
    return false;
  }
  const nextTaskIds = new Set(next.taskIds);
  return previous.taskIds.every((taskId) => nextTaskIds.has(taskId));
}
